"use client"
import React, { useState } from 'react';
import { fetchSanityData } from "@/app/actions/sanity";
import Image from 'next/image';
import Link from 'next/link';
import { useCartContext } from "@/context/CartContext";



interface project {
  slug: any;
  _id: string;
  title: string;
  price: number;
  description: string;
  discountPercentage: number;
  imageUrl: string;
  productImage: {
    asset: {
      _ref: string;
    };
  };
  tags: string[];
  categoryName: string;
  inventory?: number;
}

interface HomeproductsProps {
  products: project[];
}

const Homeproducts: React.FC<HomeproductsProps> = ({ products }) => {
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [addingItems, setAddingItems] = useState<Set<string>>(new Set());
  const { addToCart } = useCartContext();

  const handleAddToCart = (product: any) => {
    if (addingItems.has(product._id)) return;

    setAddingItems(prev => new Set(prev).add(product._id));
    addToCart({ ...product, quantity: 1 });

    setTimeout(() => {
      setAddingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(product._id);
        return newSet;
      });
      setAddedItems(prev => new Set(prev).add(product._id));

      setTimeout(() => {
        setAddedItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(product._id);
          return newSet;
        });
      }, 2000);
    }, 400);
  };

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 text-sm">
        No products available in this category.
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
        {products.map((product) => {
          const isOutOfStock = typeof product.inventory !== "undefined" && product.inventory <= 0;
          return (
            <div
              key={product._id}
              className="bg-white border border-gray-100 rounded-[24px] shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col p-3.5"
            >
              {/* Image container */}
              <Link 
                href={`/${product.slug}`} 
                className="relative overflow-hidden block aspect-square bg-[#F4F4F4] rounded-[20px] p-4 group-hover:opacity-95 transition-opacity"
              >
                <Image
                  src={product.imageUrl}
                  alt={product.title}
                  fill
                  className="object-contain p-2 group-hover:scale-105 transition-transform duration-500"
                  sizes="(max-width: 768px) 100vw, 25vw"
                />

                {isOutOfStock && (
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center rounded-[20px]">
                    <span className="bg-red-600 text-white font-black text-xs tracking-widest uppercase px-3 py-1.5 rounded-lg shadow-md">
                      Out of Stock
                    </span>
                  </div>
                )}
                
                {product.discountPercentage > 0 && !isOutOfStock && (
                  <span className="absolute top-3 left-3 text-[#B34493] font-bold text-[10px] tracking-wider uppercase bg-white/80 backdrop-blur-sm px-2 py-0.5 rounded-md">
                    WAS Rs. {Math.round(product.price / (1 - product.discountPercentage / 100)).toLocaleString()}
                  </span>
                )}
              </Link>

              {/* Info Container */}
              <div className="pt-4 px-1 flex flex-col flex-1">
                {/* Row 1: Title & Discount percentage */}
                <div className="flex justify-between items-start gap-2">
                  <Link href={`/${product.slug}`} className="min-w-0 flex-1">
                    <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate hover:text-[#B88E2F] transition-colors">
                      {product.title}
                    </h3>
                  </Link>
                  {product.discountPercentage > 0 && (
                    <span className="text-[#E23F51] font-bold text-xs sm:text-sm flex-shrink-0">
                      {product.discountPercentage}% OFF
                    </span>
                  )}
                </div>

                {/* Row 2: Description */}
                <p className="text-[11px] sm:text-xs text-gray-400 line-clamp-1 mt-0.5">
                  {product.description || "Premium School & Office Supply"}
                </p>



                {/* Row 4: Pricing & Buy Button */}
                <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-gray-50">
                  {/* Price Box */}
                  <div className="flex flex-col">
                    {product.discountPercentage > 0 && (
                      <span className="text-[10px] sm:text-xs text-gray-400 line-through leading-none mb-0.5">
                        Rs. {Math.round(product.price / (1 - product.discountPercentage / 100)).toLocaleString()}
                      </span>
                    )}
                    <span className="text-sm sm:text-base font-extrabold text-gray-900 leading-none">
                      Rs. {product.price.toLocaleString()}
                    </span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2.5">
                    {/* Add to Cart Icon Button */}
                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={addingItems.has(product._id) || isOutOfStock}
                      className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
                        isOutOfStock
                          ? "bg-gray-100 border-gray-100 text-gray-300 cursor-not-allowed"
                          : addedItems.has(product._id)
                          ? "bg-green-600 border-green-600 text-white shadow-sm"
                          : addingItems.has(product._id)
                          ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                          : "border-gray-200 hover:border-black text-gray-700 hover:text-black bg-white hover:shadow-sm"
                      }`}
                      title={isOutOfStock ? "Out of Stock" : "Add to Cart"}
                    >
                      {addingItems.has(product._id) ? (
                        <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      ) : addedItems.has(product._id) ? (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      )}
                    </button>

                    {/* Buy Now Button (Navigates to detail page) */}
                    {isOutOfStock ? (
                      <button
                        disabled
                        className="rounded-full bg-gray-100 text-gray-400 border border-gray-200 font-bold text-xs px-4 py-2.5 cursor-not-allowed whitespace-nowrap"
                      >
                        Sold Out
                      </button>
                    ) : (
                      <Link
                        href={`/${product.slug}`}
                        className="rounded-full bg-black text-white font-bold text-xs px-4 py-2.5 hover:bg-gray-800 transition text-center whitespace-nowrap shadow-sm hover:shadow"
                      >
                        Buy Now
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Homeproducts;
